import React, { useEffect, useRef } from 'react';

const O = '#FF7A1A';
const O3 = 'rgba(255,122,26,';

export default function LockCanvas() {
  const ref = useRef(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    let animId = 0, t = 0;

    const resize = () => {
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.scale(dpr, dpr);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    const W = () => canvas.offsetWidth;
    const H = () => canvas.offsetHeight;

    // Binary rain columns
    const mkRain = () => {
      const w = W();
      return Array.from({ length: Math.floor(w / 20) }, (_, i) => ({
        x: 10 + i * 20, y: Math.random() * H(),
        chars: Array.from({ length: 22 }, () => Math.random() > 0.5 ? '1' : Math.random() > 0.5 ? '0' : String.fromCharCode(0x30A0 + Math.random() * 96 | 0)),
        spd: 0.45 + Math.random() * 0.7, o: 0.05 + Math.random() * 0.12, bright: Math.random() > 0.88,
      }));
    };
    let rain = mkRain();

    // Particles
    const particles = Array.from({ length: 50 }, () => ({
      x: Math.random() * 1000, y: Math.random() * 700,
      vx: (Math.random() - 0.5) * 0.25, vy: (Math.random() - 0.5) * 0.25,
      r: 0.4 + Math.random() * 1.6, o: Math.random() * 0.5 + 0.1, od: Math.random() > 0.5 ? 1 : -1,
    }));

    // Circuit spokes from lock center
    const spokes = Array.from({ length: 8 }, (_, i) => ({
      angle: (Math.PI * 2 / 8) * i, len: 40 + Math.random() * 20
    }));

    // Orbiting dots
    const orbs = Array.from({ length: 6 }, (_, i) => ({
      angle: (Math.PI * 2 / 6) * i, r: 80 + Math.random() * 16, spd: 0.007 + Math.random() * 0.005
    }));

    const drawGrid = (w, h) => {
      ctx.strokeStyle = O3 + '.055)'; ctx.lineWidth = 0.5;
      const sz = 40;
      for (let x = 0; x < w; x += sz) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
      for (let y = 0; y < h; y += sz) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }
    };

    const drawLock = (cx, cy) => {
      const phase = (Math.sin(t * 0.65) + 1) / 2;
      const bw = 66, bh = 52;

      // Pulse rings
      for (let i = 0; i < 4; i++) {
        const p = (phase + i / 4) % 1;
        ctx.beginPath(); ctx.arc(cx, cy + 10, 50 + p * 65, 0, Math.PI * 2);
        ctx.strokeStyle = O3 + ((1 - p) * 0.18) + ')'; ctx.lineWidth = 1; ctx.stroke();
      }

      // Body
      ctx.shadowColor = O; ctx.shadowBlur = 28 + phase * 18;
      const bx = cx - bw / 2, by = cy - 2;
      ctx.beginPath();
      ctx.moveTo(bx + 8, by); ctx.lineTo(bx + bw - 8, by);
      ctx.arcTo(bx + bw, by, bx + bw, by + 8, 8); ctx.lineTo(bx + bw, by + bh - 8);
      ctx.arcTo(bx + bw, by + bh, bx + bw - 8, by + bh, 8); ctx.lineTo(bx + 8, by + bh);
      ctx.arcTo(bx, by + bh, bx, by + bh - 8, 8); ctx.lineTo(bx, by + 8);
      ctx.arcTo(bx, by, bx + 8, by, 8); ctx.closePath();
      const bg = ctx.createLinearGradient(bx, by, bx + bw, by + bh);
      bg.addColorStop(0, 'rgba(38,16,0,0.82)'); bg.addColorStop(1, 'rgba(18,8,0,0.82)');
      ctx.fillStyle = bg; ctx.fill();
      ctx.strokeStyle = O3 + (0.75 + phase * 0.25) + ')'; ctx.lineWidth = 2; ctx.stroke();

      // Shackle
      ctx.shadowColor = O; ctx.shadowBlur = 18 + phase * 8;
      ctx.beginPath(); ctx.arc(cx, by, 20, Math.PI, 0);
      ctx.strokeStyle = O3 + (0.9 + phase * 0.1) + ')'; ctx.lineWidth = 4.5; ctx.lineCap = 'round'; ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx - 20, by); ctx.lineTo(cx - 20, by + 12);
      ctx.moveTo(cx + 20, by); ctx.lineTo(cx + 20, by + 12); ctx.stroke();
      ctx.lineCap = 'butt';

      // Keyhole
      ctx.shadowColor = O; ctx.shadowBlur = 10;
      ctx.beginPath(); ctx.arc(cx, cy + 20, 8, 0, Math.PI * 2);
      ctx.fillStyle = O3 + (0.22 + phase * 0.12) + ')'; ctx.fill();
      ctx.strokeStyle = O3 + '.75)'; ctx.lineWidth = 1.5; ctx.stroke();
      ctx.beginPath(); ctx.moveTo(cx, cy + 27); ctx.lineTo(cx, cy + 38);
      ctx.lineWidth = 4; ctx.stroke();
      ctx.shadowBlur = 0;

      // Circuit spokes
      spokes.forEach((s, i) => {
        const a = s.angle, len = s.len + Math.sin(t + i) * 0.08 * s.len;
        const x1 = cx + Math.cos(a) * 52, y1 = cy + 10 + Math.sin(a) * 52;
        const x2 = cx + Math.cos(a) * (52 + len), y2 = cy + 10 + Math.sin(a) * (52 + len);
        ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
        ctx.strokeStyle = O3 + (0.15 + Math.sin(t * 2 + i) * 0.08) + ')'; ctx.lineWidth = 1; ctx.stroke();
        ctx.beginPath(); ctx.arc(x2, y2, 2, 0, Math.PI * 2);
        ctx.fillStyle = O3 + '.5)'; ctx.fill();
      });
    };

    const draw = () => {
      t += 0.011;
      const w = W(), h = H();
      ctx.clearRect(0, 0, w, h);

      const ag = ctx.createRadialGradient(w * 0.5, h * 0.5, 0, w * 0.5, h * 0.5, w * 0.55);
      ag.addColorStop(0, 'rgba(55,22,0,0.12)'); ag.addColorStop(1, 'rgba(11,11,12,0)');
      ctx.fillStyle = ag; ctx.fillRect(0, 0, w, h);

      drawGrid(w, h);

      // Binary rain
      rain.forEach(d => {
        d.y += d.spd; if (d.y > h + 280) d.y = -200;
        d.chars.forEach((c, i) => {
          const ry = d.y + i * 14; if (ry < 0 || ry > h) return;
          const alpha = d.bright && i === d.chars.length - 1 ? 0.85 : d.o * (1 - i / d.chars.length);
          ctx.fillStyle = d.bright && i === d.chars.length - 1 ? `rgba(255,180,100,${alpha})` : O3 + alpha + ')';
          ctx.font = d.bright && i === d.chars.length - 1 ? 'bold 10px monospace' : '9px monospace';
          ctx.fillText(c, d.x, ry);
        });
      });

      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.o += p.od * 0.005;
        if (p.o >= 0.65 || p.o <= 0.05) p.od *= -1;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0; if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = O3 + (p.o * 0.45) + ')'; ctx.fill();
      });

      // Orbiters
      orbs.forEach(o => {
        o.angle += o.spd;
        const ox = w * 0.5 + Math.cos(o.angle) * o.r;
        const oy = h * 0.46 + Math.sin(o.angle) * o.r;
        ctx.shadowColor = O; ctx.shadowBlur = 8;
        ctx.beginPath(); ctx.arc(ox, oy, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = O3 + '.75)'; ctx.fill(); ctx.shadowBlur = 0;
      });

      // Orbit ring
      ctx.beginPath(); ctx.arc(w * 0.5, h * 0.46, 88, 0, Math.PI * 2);
      ctx.strokeStyle = O3 + '.08)'; ctx.lineWidth = 1; ctx.setLineDash([4, 10]); ctx.stroke();
      ctx.setLineDash([]);

      drawLock(w * 0.5, h * 0.46);

      const sy = ((t * 36) % (h + 80)) - 40;
      const sg = ctx.createLinearGradient(0, sy - 2, 0, sy + 2);
      sg.addColorStop(0, 'transparent'); sg.addColorStop(0.5, O3 + '.04)'); sg.addColorStop(1, 'transparent');
      ctx.fillStyle = sg; ctx.fillRect(0, sy - 2, w, 4);

      animId = requestAnimationFrame(draw);
    };

    draw();
    return () => { cancelAnimationFrame(animId); ro.disconnect(); };
  }, []);

  return <canvas ref={ref} style={{ width: '100%', height: '100%', display: 'block' }} />;
}
